import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  EDITOR_CALL_SHEETS_NAV_ITEM,
  EDITOR_NAV_GROUPS,
  EDITOR_VIEWS,
} from '../editor/editorNavigation';
import { REPORT_CATALOG } from '../../models/reportCatalog';
import { getExportAction, type ExportActionId } from '../../models/exportActions';
import { PROJECT_ACCESS_ROLES, PROJECT_ROLE_LABELS } from '../../models/projectAccess';
import { PRODUCTION_WORK_STATUS_PRESENTATIONS } from '../../models/productionWorkStatus';
import { DEMO_PROJECT_METADATA } from '../../data/demoProject';
import LandingHeader from './LandingHeader';
import LandingFooter from './LandingFooter';
import { ArrowIcon, CheckIcon, PlayIcon } from './LandingIcons';
import {
  BreakdownSheetVisual,
  CallSheetVisual,
  DoodVisual,
  HeroVisual,
  MoodBoardVisual,
  ReferencesVisual,
  SidesVisual,
  StoryboardVisual,
  StripboardVisual,
  StripLegend,
} from './LandingVisuals';
import {
  ACCESS_OPTIONS,
  ACCESS_POINTS,
  BREAKDOWN_POINTS,
  EXTRA_OUTPUTS,
  FAQ_ITEMS,
  REPORT_SUMMARIES,
  ROLE_CAPABILITIES,
  ROLE_CAPABILITY_LABELS,
  type RoleCapability,
  SCHEDULE_POINTS,
  SCRIPT_IMPORT_FORMATS,
  WORKFLOW_STEPS,
} from './landingContent';
import './landing.css';

const DEMO_PATH = '/editor/demo/script';

const formatsFor = (actionIds: readonly ExportActionId[]): string[] => (
  actionIds.map(actionId => getExportAction(actionId).shortLabel)
);

const PointList: React.FC<{ points: readonly string[] }> = ({ points }) => (
  <ul className="lp-points">
    {points.map(point => <li key={point}>{point}</li>)}
  </ul>
);

const ChapterHead: React.FC<{ number: string; label: string; title: string; children: React.ReactNode }> = ({
  number, label, title, children,
}) => (
  <div className="lp-chapter-head">
    <p className="lp-chapter-label"><span>{number}</span>{label}</p>
    <h3 className="lp-chapter-title">{title}</h3>
    <div className="lp-chapter-lede">{children}</div>
  </div>
);

const MoreLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link to={to} className="lp-more">{children}<ArrowIcon /></Link>
);

const Hero: React.FC = () => (
  <section className="lp-hero" aria-labelledby="lp-hero-title">
    <div className="lp-wrap lp-hero-grid">
      <div className="lp-hero-copy">
        <p className="lp-kicker">Film pre-production workspace</p>
        <h1 id="lp-hero-title" className="lp-hero-title">
          <span>Break down the script.</span> <em>Board the shoot.</em> <span>Print the call sheets.</span>
        </h1>
        <p className="lp-hero-lede">
          EzyPrep turns a screenplay into scenes and tagged production elements, then carries those same scenes
          through your stripboard, shooting calendar, DOOD, sides, storyboards and call sheets.
        </p>
        <div className="lp-actions">
          <Link to="/try-free" className="lp-btn lp-btn-primary">
            Create Free Account <ArrowIcon />
          </Link>
          <Link to={DEMO_PATH} className="lp-btn lp-btn-ghost">
            <PlayIcon /> Explore Demo
          </Link>
        </div>
        <p className="lp-hero-note">
          Free accounts include 5 AI breakdown projects. Manual breakdown, scheduling and exports are included too.
        </p>
        <p className="lp-formats">
          <span className="lp-formats-label">Imports</span>
          {SCRIPT_IMPORT_FORMATS.map(format => <span key={format} className="lp-format">{format}</span>)}
        </p>
      </div>
      <HeroVisual />
    </div>
  </section>
);

const Facts: React.FC = () => {
  const facts = [
    { value: EDITOR_VIEWS.length, label: 'Planning views' },
    { value: SCRIPT_IMPORT_FORMATS.length, label: 'Script formats' },
    { value: REPORT_CATALOG.length, label: 'Report surfaces' },
    { value: PROJECT_ACCESS_ROLES.length, label: 'Project roles' },
  ];
  return (
    <section className="lp-facts" aria-label="EzyPrep at a glance">
      <dl className="lp-wrap lp-facts-row">
        {facts.map(fact => (
          <div key={fact.label} className="lp-fact">
            <dt>{fact.label}</dt>
            <dd className="lp-fact-number">{fact.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const Workflow: React.FC = () => (
  <section id="testimonials" className="lp-section lp-workflow" aria-labelledby="lp-workflow-title">
    <div className="lp-wrap">
      <div className="lp-section-head">
        <p className="lp-eyebrow">Workflow</p>
        <h2 id="lp-workflow-title" className="lp-section-title">How a project moves through EzyPrep</h2>
        <p className="lp-section-lede">
          The order a prep office already works in, with the same scenes carried from each step to the next.
        </p>
      </div>
      <ol className="lp-steps">
        {WORKFLOW_STEPS.map((step, index) => (
          <li key={step.label} className="lp-step">
            <span className="lp-step-no">{String(index + 1).padStart(2, '0')}</span>
            <span className="lp-step-label">{step.label}</span>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const WorkspaceMap: React.FC = () => (
  <div className="lp-map" aria-label="Views in the editor">
    {EDITOR_NAV_GROUPS.map(group => (
      <div key={group.key} className="lp-map-group">
        <span className="lp-map-title">{group.label}</span>
        <ul>
          {group.items.map(item => <li key={item.view}>{item.label}</li>)}
        </ul>
      </div>
    ))}
    <div className="lp-map-group">
      <span className="lp-map-title">{EDITOR_CALL_SHEETS_NAV_ITEM.label}</span>
      <ul><li>{EDITOR_CALL_SHEETS_NAV_ITEM.label}</li></ul>
      <p className="lp-map-note">One per shooting day, built from the schedule.</p>
    </div>
  </div>
);

const ExportsTable: React.FC = () => (
  <div className="lp-exports">
    <h4 id="lp-exports-title" className="lp-exports-title">What you can export</h4>
    <div className="lp-table-scroll">
      <table className="lp-table" aria-labelledby="lp-exports-title">
        <thead>
          <tr><th scope="col">Output</th><th scope="col">What it covers</th><th scope="col">Formats</th></tr>
        </thead>
        <tbody>
          {REPORT_CATALOG.map(report => (
            <tr key={report.id}>
              <th scope="row">{report.label}</th>
              <td>{REPORT_SUMMARIES[report.id]}</td>
              <td><span className="lp-format-list">{formatsFor(report.exportActionIds).map(format => <span key={format} className="lp-format">{format}</span>)}</span></td>
            </tr>
          ))}
          {EXTRA_OUTPUTS.map(output => (
            <tr key={output.label}>
              <th scope="row">{output.label}</th>
              <td>{output.description}</td>
              <td><span className="lp-format-list">{formatsFor(output.actionIds).map(format => <span key={format} className="lp-format">{format}</span>)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const RolesTable: React.FC = () => {
  const capabilities = Object.keys(ROLE_CAPABILITY_LABELS) as RoleCapability[];
  return (
    <div className="lp-panel lp-roles">
      <div className="lp-table-scroll">
        <table className="lp-table lp-roles-table">
          <caption className="lp-visually-hidden">What each project role can do</caption>
          <thead>
            <tr>
              <th scope="col">Role</th>
              {capabilities.map(capability => <th key={capability} scope="col">{ROLE_CAPABILITY_LABELS[capability]}</th>)}
            </tr>
          </thead>
          <tbody>
            {PROJECT_ACCESS_ROLES.map(role => (
              <tr key={role}>
                <th scope="row">{PROJECT_ROLE_LABELS[role]}</th>
                {capabilities.map(capability => {
                  const allowed = ROLE_CAPABILITIES[role].includes(capability);
                  return (
                    <td key={capability} className={allowed ? 'is-yes' : 'is-no'}>
                      {allowed ? <CheckIcon /> : <span aria-hidden="true">—</span>}
                      <span className="lp-visually-hidden">{allowed ? 'Yes' : 'No'}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Features: React.FC = () => (
  <section id="features" className="lp-section lp-features" aria-labelledby="lp-features-title">
    <div className="lp-wrap">
      <div className="lp-section-head lp-section-head-split">
        <div>
          <p className="lp-eyebrow">The workspace</p>
          <h2 id="lp-features-title" className="lp-section-title">{EDITOR_VIEWS.length} views. One set of scenes.</h2>
        </div>
        <p className="lp-section-lede">
          Every view reads from the same project scenes, so the breakdown you correct is the one you schedule,
          print and share.
        </p>
      </div>
      <WorkspaceMap />

      <article className="lp-chapter">
        <ChapterHead number="01" label="Breakdown & script" title="Read the script once. Tag it for every department.">
          <p>
            Choose AI and EzyPrep finds the scenes and tags cast, extras, props, costumes, vehicles, sound, VFX,
            stunts, animals, makeup and hair and more. Choose Manual and tag them yourself. Either way, each
            element stays pinned to the scene it came from.
          </p>
          <PointList points={BREAKDOWN_POINTS} />
          <MoreLink to="/features/script-analysis">More on AI breakdown</MoreLink>
        </ChapterHead>
        <div className="lp-chapter-visual">
          <BreakdownSheetVisual />
        </div>
      </article>

      <article className="lp-chapter lp-chapter-flip">
        <ChapterHead number="02" label="Stripboard & calendar" title="Board it like an AD. Undo it like a document.">
          <p>
            Strips follow the classic colour code, so the board reads at a glance. Drag them into shooting order,
            drop in day breaks and banners, and check each day before anyone gets a call time.
          </p>
          <StripLegend />
          <PointList points={SCHEDULE_POINTS} />
          <MoreLink to="/features/scheduling">More on scheduling</MoreLink>
        </ChapterHead>
        <div className="lp-chapter-visual">
          <StripboardVisual />
        </div>
      </article>

      <article className="lp-chapter lp-chapter-stack">
        <ChapterHead number="03" label="Call sheets, sides & DOOD" title="The paperwork starts from the schedule, not a blank page.">
          <p>
            Once your shooting days are set, each day’s scenes, cast and pages are already in place. You add the
            times, contacts and notes only you know.
          </p>
        </ChapterHead>
        <div className="lp-paperwork">
          <div className="lp-card lp-card-callsheet">
            <CallSheetVisual />
            <h4>Call Sheets</h4>
            <p>
              One per shooting day: crew call, lunch and wrap, key crew, safety and the nearest hospital,
              locations with parking, the scene schedule, cast pickup, makeup and on-set times, background,
              department notes and the advance schedule.
            </p>
          </div>
          <div className="lp-card">
            <SidesVisual />
            <h4>Sides</h4>
            <p>
              Pick a shooting day and its scenes become sides. Highlight by cast or category, cross out lines,
              add notes, and attach the call sheet for one daily packet.
            </p>
          </div>
          <div className="lp-card">
            <DoodVisual />
            <h4>Day out of days</h4>
            <p>Worked out from the schedule for cast, extras or every element.</p>
            <ul className="lp-codes" aria-label="DOOD codes">
              {PRODUCTION_WORK_STATUS_PRESENTATIONS.map(status => (
                <li key={status.code}><strong>{status.code}</strong> {status.doodLabel}</li>
              ))}
            </ul>
          </div>
        </div>
        <ExportsTable />
      </article>

      <article className="lp-chapter lp-chapter-stack">
        <ChapterHead number="04" label="Storyboards, mood boards & references" title="Plan the look shot by shot. Keep real references real.">
          <p>
            Storyboard each scene with shot type, angle, movement and lens, shared with the shot list. Generate a
            frame with AI or upload your own. References only ever come from photo libraries and your uploads,
            never from the image generator.
          </p>
        </ChapterHead>
        <StoryboardVisual />
        <div className="lp-visual-pair">
          <div className="lp-card">
            <MoodBoardVisual />
            <h4>Mood Board</h4>
            <p>
              Boards for mood, character looks, locations and costume. Generate images from the script, pull a
              palette from any image, and export a PDF.
            </p>
          </div>
          <div className="lp-card">
            <ReferencesVisual />
            <h4>References</h4>
            <p>
              Search four photo libraries or upload your own. Comment on images, present them, export a PDF, or
              share a view-only board for 7, 30 or 90 days.
            </p>
          </div>
        </div>
      </article>

      <article className="lp-chapter">
        <ChapterHead number="05" label="Team & access" title="Everyone sees the plan. Not everyone can change it.">
          <p>
            Share a project with {PROJECT_ACCESS_ROLES.length} roles. Owners keep the breakdown and access,
            ADs and editors run the schedule and call sheets, and viewers read and export.
          </p>
          <PointList points={ACCESS_POINTS} />
          <div className="lp-more-row">
            <MoreLink to="/features/collaboration">Collaboration</MoreLink>
            <MoreLink to="/features/security">Security</MoreLink>
          </div>
        </ChapterHead>
        <div className="lp-chapter-visual">
          <RolesTable />
        </div>
      </article>
    </div>
  </section>
);

const DemoBand: React.FC = () => (
  <section className="lp-demo" aria-labelledby="lp-demo-title">
    <img className="lp-demo-image" src={DEMO_PROJECT_METADATA.thumbnailUrl} alt="" loading="lazy" decoding="async" />
    <div className="lp-wrap lp-demo-inner">
      <p className="lp-eyebrow">No account needed</p>
      <h2 id="lp-demo-title" className="lp-section-title">Try it on {DEMO_PROJECT_METADATA.title}</h2>
      <p className="lp-section-lede">
        A {DEMO_PROJECT_METADATA.sceneCount}-scene sci-fi {DEMO_PROJECT_METADATA.type.toLowerCase()}, already
        broken down. Rearrange the strips, add shooting days and open every view before you start a project of
        your own.
      </p>
      <div className="lp-actions">
        <Link to={DEMO_PATH} className="lp-btn lp-btn-primary"><PlayIcon /> Open the demo project</Link>
      </div>
    </div>
  </section>
);

const Pricing: React.FC = () => (
  <section id="pricing" className="lp-section lp-pricing" aria-labelledby="lp-pricing-title">
    <div className="lp-wrap">
      <div className="lp-section-head">
        <p className="lp-eyebrow">Pricing</p>
        <h2 id="lp-pricing-title" className="lp-section-title">Start free. Talk to us when you need more AI.</h2>
        <p className="lp-section-lede">
          Start with 5 free AI breakdown projects, plus manual planning and exports. Paid AI access and enterprise
          arrangements are activated directly; self-service billing is not available yet.
        </p>
      </div>
      <div className="lp-plans">
        {ACCESS_OPTIONS.map(option => (
          <div key={option.name} className={`lp-plan${option.badge ? ' is-featured' : ''}`}>
            <div className="lp-plan-top">
              <h3>{option.name}</h3>
              {option.badge && <span className="lp-plan-badge">{option.badge}</span>}
            </div>
            <p className="lp-plan-price">{option.priceLabel}</p>
            <p className="lp-plan-desc">{option.description}</p>
            <ul className="lp-plan-list">
              {option.features.map(feature => <li key={feature}><CheckIcon />{feature}</li>)}
            </ul>
            {option.action.internal ? (
              <Link to={option.action.href} className="lp-btn lp-btn-primary lp-plan-cta">{option.action.label}</Link>
            ) : (
              <a href={option.action.href} className="lp-btn lp-btn-ghost lp-plan-cta">{option.action.label}</a>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Faq: React.FC = () => (
  <section id="faq" className="lp-section lp-faq" aria-labelledby="lp-faq-title">
    <div className="lp-wrap lp-faq-grid">
      <div className="lp-section-head">
        <p className="lp-eyebrow">FAQ</p>
        <h2 id="lp-faq-title" className="lp-section-title">Straight answers</h2>
        <p className="lp-section-lede">What EzyPrep does today, and what it doesn’t do yet.</p>
      </div>
      <div className="lp-faq-list">
        {FAQ_ITEMS.map(item => (
          <details key={item.question} className="lp-faq-item">
            <summary>{item.question}<span className="lp-faq-sign" aria-hidden="true" /></summary>
            <p>
              {item.answer}
              {item.link && <> <Link to={item.link.to} className="lp-inline-link">{item.link.label}</Link>.</>}
            </p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta: React.FC = () => (
  <section className="lp-final" aria-labelledby="lp-final-title">
    <div className="lp-slate-stripe" aria-hidden="true" />
    <div className="lp-wrap lp-final-inner">
      <h2 id="lp-final-title" className="lp-final-title">Your next shoot starts on page one.</h2>
      <p className="lp-section-lede">Upload a script, or open the demo and walk through the whole workflow first.</p>
      <div className="lp-actions">
        <Link to="/try-free" className="lp-btn lp-btn-primary">Create Free Account <ArrowIcon /></Link>
        <Link to={DEMO_PATH} className="lp-btn lp-btn-ghost"><PlayIcon /> Explore Demo</Link>
      </div>
    </div>
  </section>
);

const LANDING_FONTS = ['400 1em "Instrument Serif"', 'italic 400 1em "Instrument Serif"', '400 1em "Courier Prime"'];

/**
 * The display serif is narrower than its fallback, so headings get shorter
 * when it arrives and everything below moves up. Scrolling to a section
 * before that lands short of it, so wait for the fonts (briefly) first.
 */
const waitForLandingFonts = (): Promise<unknown> => {
  if (!('fonts' in document) || typeof document.fonts.load !== 'function') return Promise.resolve();
  return Promise.race([
    Promise.all(LANDING_FONTS.map(font => document.fonts.load(font))).catch(() => undefined),
    new Promise(resolve => window.setTimeout(resolve, 1500)),
  ]);
};

const sectionIdFromHash = (hash: string): string => {
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
};

const LandingPage: React.FC = () => {
  const location = useLocation();

  // Arriving at /#pricing (from a feature page, or a nav link on this page)
  // scrolls to that section once it has rendered.
  useEffect(() => {
    if (!location.hash) return undefined;
    let cancelled = false;
    let frame = 0;
    waitForLandingFonts().then(() => {
      if (cancelled) return;
      frame = window.requestAnimationFrame(() => {
        const target = document.getElementById(sectionIdFromHash(location.hash));
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        target?.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [location.hash, location.key]);

  return (
    <div className="lp">
      <LandingHeader />
      <main>
        <Hero />
        <Facts />
        <Workflow />
        <Features />
        <DemoBand />
        <Pricing />
        <Faq />
        <FinalCta />
      </main>
      <LandingFooter />
    </div>
  );
};

export default LandingPage;
